import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ANALYTIC_EVENTS, Analytics } from 'analytics/Analytics';
import EmailSendIcon from 'assets/icons/base/email-send-icon.svg';
import { SEARCH_PARAMS } from 'constants/search-params';
import { useTranslate, useValidate, useYM } from 'hooks';
import { useAppSelector } from 'store/hooks';
import { useSendCheckMutation } from 'store/payment/service';
import { getQueryParams } from 'utils';
import { Button, Input } from 'views/components/base/ui';
import { SpinnerIcon } from 'views/shared/spinner-icon';

type EmailFormType = {
  email: string;
};

const SendCheckForCheckview = () => {
  const { paymentData } = useAppSelector((state) => state.payment);
  const { control, handleSubmit, reset } = useForm<EmailFormType>({
    mode: 'onBlur',
    defaultValues: {
      email: '',
    },
  });
  const [isSendCheck, setIsSendCheck] = useState(false);
  const [checkSended, setCheckSended] = useState(false);
  const [check, { isLoading, isSuccess }] = useSendCheckMutation();
  const { trackOnClick } = useYM();
  const { validateEmail } = useValidate();

  const { t } = useTranslate();

  const toggleSendEmailForm = () => {
    setIsSendCheck(!isSendCheck);
  };

  const handleSendCheck = async (data: EmailFormType) => {
    trackOnClick('Отправить чек на почту');

    if (paymentData) {
      const checkData = {
        ID: paymentData.ID,
        email: data.email,
      };

      check({ ID: paymentData.ID, check: checkData })
        .unwrap()
        .then(() => {
          Analytics.sentEvent(ANALYTIC_EVENTS.checkSendSuccess, {
            client_email: checkData.email,
          });

          setCheckSended(true);
        })
        .catch((error) => {
          Analytics.sentEvent(ANALYTIC_EVENTS.checkSendError, {
            transaction_id: getQueryParams(SEARCH_PARAMS.ID),
            message: error.message,
            client_email: checkData.email,
          });
        });
    }
  };

  useEffect(() => {
    if (isSuccess && checkSended) {
      const timeoutId = setTimeout(() => {
        setIsSendCheck(false);
        setCheckSended(false);
        reset();
      }, 3000);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return undefined;
  }, [checkSended, isSuccess, reset]);

  const renderEmailSteps = () => {
    if (checkSended) {
      return (
        <div className="flex items-center justify-center gap-2 h-8 md:h-9">
          <i className="ri-checkbox-circle-line text-xl" />
          {t('lang.check_email_success_send')}
        </div>
      );
    }

    if (isSendCheck) {
      return (
        <form className="flex items-center gap-2.5 w-full h-8 md:h-9">
          <Controller
            name="email"
            control={control}
            rules={{ required: true, validate: validateEmail }}
            render={({ field: { value, ref, onChange, onBlur }, fieldState: { error } }) => (
              <Input
                ref={ref}
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                error={error?.message}
                name="email"
                containerClassName="w-full"
                inputClassName="leading-[12px] h-[21px] text-[15px] md:h-[21px] md:text-[15px] align-middle border-b bg-[transparent] pb-2"
              />
            )}
          />

          <Button
            className="h-8 w-8 md:h-9 md:w-9 md:rounded-xsmall p-0 md:p-0 bg-base-yellow hover:bg-base-yellow active:bg-base-yellow text-black flex justify-center items-center"
            onClick={handleSubmit(handleSendCheck)}
          >
            {isLoading ? (
              <SpinnerIcon className="stroke-black" />
            ) : (
              <img src={EmailSendIcon} className="w-3.5" alt="send check" />
            )}
          </Button>
        </form>
      );
    }

    return (
      <Button
        onClick={toggleSendEmailForm}
        className="h-8 md:h-9 font-medium md:rounded-xsmall md:py-2 bg-base-yellow hover:bg-base-yellow active:bg-base-yellow text-black"
      >
        {t('send_email')} <i className="ri-mail-line align-middle" />
      </Button>
    );
  };

  return renderEmailSteps();
};

export { SendCheckForCheckview };
